import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Camera,
  Check,
  ChevronDown,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { TbView360Number } from "react-icons/tb";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Exterior360CaptureModal } from "@/components/exterior360/Exterior360CaptureModal";
import {
  Exterior360SegmentPicker,
  type ImageByIndex,
} from "@/components/exterior360/Exterior360SegmentPicker";
import { Exterior360Viewer } from "@/components/exterior360/Exterior360Viewer";
import { useExterior360UploadQueue } from "@/hooks/useExterior360UploadQueue";
import { cn } from "@/lib/utils";
import { TestService } from "@/services/test/testService";
import { CarTestService } from "@/services/carTest/carTestService";
import { VehicleTest } from "@/types/vehicleTest";
import { useLanguage } from "@/context/LanguageContext";
import { exterior360Labels } from "@/i18n/labels/exterior360Labels";
import { carTestLabels } from "@/i18n/labels";

/**
 * Admin-only POC page for 360 exterior view capture.
 * Access via /360-test (no UI link).
 */
export default function Exterior360Test() {
  const { language } = useLanguage();
  const labels = exterior360Labels(language);
  const ct = carTestLabels(language);

  const getStatusLabel = (status: string) => {
    const key =
      status === "pending-review"
        ? "pendingReview"
        : status === "active-listing"
          ? "activeListing"
          : status;
    return (ct.status as Record<string, string>)?.[key] ?? status;
  };
  const [selectedTestId, setSelectedTestId] = useState<string | null>(null);
  const [selectedTest, setSelectedTest] = useState<VehicleTest | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewerUrls, setViewerUrls] = useState<string[]>([]);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fullscreenUrls, setFullscreenUrls] = useState<string[]>([]);
  const [fullscreenLoading, setFullscreenLoading] = useState(false);
  const [segmentFullscreenImage, setSegmentFullscreenImage] = useState<{
    url: string;
    index: number;
  } | null>(null);
  const [viewerImagesByIndex, setViewerImagesByIndex] = useState<
    Record<number, ImageByIndex>
  >({});
  const [modalOpenForFrame, setModalOpenForFrame] = useState<number | null>(
    null,
  );
  const [deletingFrameIndex, setDeletingFrameIndex] = useState<number | null>(
    null,
  );
  const [isDeletingAll360, setIsDeletingAll360] = useState(false);
  const [testSelectOpen, setTestSelectOpen] = useState(false);
  const [testSearchQuery, setTestSearchQuery] = useState("");
  const queryClient = useQueryClient();
  const {
    enqueue,
    doneCount,
    failedCount,
    allDone,
    total: uploadTotal,
    queue,
  } = useExterior360UploadQueue(selectedTestId);
  const uploadingFrameIndices = useMemo(
    () =>
      queue
        .filter(
          (i) =>
            i.status === "pending" ||
            i.status === "compressing" ||
            i.status === "uploading",
        )
        .map((i) => i.frameIndex),
    [queue],
  );

  const { data: testsData } = useQuery({
    queryKey: ["admin-tests", "360-select", 1, 100],
    queryFn: () =>
      TestService.getAllTestsForAdmin(1, 100, "ALL", undefined, true),
    staleTime: 60_000,
  });

  const allTests = (testsData?.tests ?? []) as VehicleTest[];
  const viewerSectionRef = useRef<HTMLDivElement>(null);

  const filteredTests = useMemo(() => {
    const q = testSearchQuery.trim().toLowerCase();
    if (!q) return allTests;
    return allTests.filter((t) => {
      const reg = (
        t.vehicleDetails?.regNumber ||
        t.regNumber ||
        ""
      ).toLowerCase();
      const name = (t.vehicleName || "").toLowerCase();
      return reg.includes(q) || name.includes(q);
    });
  }, [allTests, testSearchQuery]);

  const { data: testsWith360 = [], isLoading: loading360List } = useQuery({
    queryKey: ["admin-tests-360", allTests.map((t) => t.id)],
    queryFn: async () => {
      const limit = Math.min(allTests.length, 100);
      const results = await Promise.allSettled(
        allTests.slice(0, limit).map(async (t) => {
          const images = await CarTestService.getCarTestImages(
            t.id,
            "EXTERIOR",
          );
          const withFrame = images.filter((i) => i.frameIndex != null);
          if (withFrame.length === 0) return null;
          const sorted = [...withFrame].sort(
            (a, b) => (a.frameIndex ?? 0) - (b.frameIndex ?? 0),
          );
          const urls = sorted.map((i) => i.url).filter(Boolean);
          return { test: t, urls, imageCount: urls.length };
        }),
      );
      return results
        .filter(
          (
            r,
          ): r is PromiseFulfilledResult<{
            test: VehicleTest;
            urls: string[];
            imageCount: number;
          } | null> => r.status === "fulfilled" && r.value !== null,
        )
        .map(
          (r) =>
            r.value as {
              test: VehicleTest;
              urls: string[];
              imageCount: number;
            },
        )
        .filter((v) => v.imageCount > 0);
    },
    enabled: allTests.length > 0,
    staleTime: 60_000,
  });

  const loadViewerImages = useCallback(
    async (testId: string, usePreview = true) => {
      try {
        const images = await CarTestService.getCarTestImages(
          testId,
          "EXTERIOR",
          usePreview,
        );
        const withFrameIndex = images.filter((i) => i.frameIndex != null);
        if (withFrameIndex.length === 0) return [];
        const sorted = [...withFrameIndex].sort(
          (a, b) => (a.frameIndex ?? 0) - (b.frameIndex ?? 0),
        );
        return sorted.map((i) => i.url).filter(Boolean);
      } catch {
        return [];
      }
    },
    [],
  );

  const loadPreviewUrls = useCallback(
    async (testId: string) => {
      const urls = await loadViewerImages(testId, true);
      setViewerUrls(urls);
    },
    [loadViewerImages],
  );

  useEffect(() => {
    if (!selectedTestId || viewerUrls.length === 0) {
      setViewerImagesByIndex({});
      return;
    }
    Promise.all([
      CarTestService.getCarTestImages(selectedTestId, "EXTERIOR", true),
      CarTestService.getCarTestImages(selectedTestId, "EXTERIOR", false),
    ])
      .then(([previewImages, fullsizeImages]) => {
        const byIndex: Record<number, ImageByIndex> = {};
        previewImages
          .filter((i) => i.frameIndex != null)
          .forEach((i) => {
            const idx = i.frameIndex!;
            if (idx >= 0 && idx < 72 && i.url) {
              const full = fullsizeImages.find((f) => f.id === i.id);
              byIndex[idx] = { id: i.id, url: i.url, urlFull: full?.url };
            }
          });
        setViewerImagesByIndex(byIndex);
      })
      .catch(() => setViewerImagesByIndex({}));
  }, [selectedTestId, viewerUrls.length]);

  const handleDeleteSegmentImage = useCallback(
    async (frameIndex: number, imageId: string) => {
      if (!selectedTestId) return;
      setDeletingFrameIndex(frameIndex);
      try {
        await CarTestService.removeCarTestImage(selectedTestId, imageId);
        await loadPreviewUrls(selectedTestId);
        queryClient.invalidateQueries({ queryKey: ["admin-tests-360"] });
      } catch (err) {
      } finally {
        setDeletingFrameIndex(null);
      }
    },
    [selectedTestId, loadPreviewUrls, queryClient],
  );

  const handleDeleteAll360 = useCallback(async () => {
    if (!selectedTestId) return;
    if (
      !window.confirm(
        labels.deleteAll360Confirm ??
          "Vill du radera alla 360-bilder för detta test? Detta kan inte ångras.",
      )
    )
      return;
    setIsDeletingAll360(true);
    try {
      const images = await CarTestService.getCarTestImages(
        selectedTestId,
        "EXTERIOR",
        true,
      );
      const withFrame = images.filter((i) => i.frameIndex != null);
      await Promise.all(
        withFrame.map((i) =>
          CarTestService.removeCarTestImage(selectedTestId, i.id),
        ),
      );
      setViewerUrls([]);
      setViewerImagesByIndex({});
      queryClient.invalidateQueries({ queryKey: ["admin-tests-360"] });
    } catch (err) {
    } finally {
      setIsDeletingAll360(false);
    }
  }, [selectedTestId, queryClient, labels.deleteAll360Confirm]);

  const handleFullscreenClick = useCallback(async () => {
    if (!selectedTestId) return;
    setFullscreenOpen(true);
    setFullscreenLoading(true);
    setFullscreenUrls([]);
    try {
      const urls = await loadViewerImages(selectedTestId, false);
      setFullscreenUrls(urls);
    } finally {
      setFullscreenLoading(false);
    }
  }, [selectedTestId, loadViewerImages]);

  const handleSelectTest = useCallback(
    (id: string, test?: VehicleTest) => {
      setSelectedTestId(id || null);
      setSelectedTest(test ?? null);
      if (id) {
        loadPreviewUrls(id);
      } else {
        setViewerUrls([]);
      }
    },
    [loadPreviewUrls],
  );

  const handleModalChange = useCallback(
    (open: boolean) => {
      setModalOpen(open);
      if (!open) {
        setModalOpenForFrame(null);
        if (selectedTestId) loadPreviewUrls(selectedTestId);
        queryClient.invalidateQueries({ queryKey: ["admin-tests-360"] });
      }
    },
    [selectedTestId, loadPreviewUrls, queryClient],
  );

  const handleModalUploadsComplete = useCallback(() => {
    if (selectedTestId) loadPreviewUrls(selectedTestId);
    queryClient.invalidateQueries({ queryKey: ["admin-tests-360"] });
  }, [selectedTestId, loadPreviewUrls, queryClient]);

  const prevAllDoneRef = useRef(false);
  useEffect(() => {
    if (
      allDone &&
      !prevAllDoneRef.current &&
      uploadTotal > 0 &&
      selectedTestId
    ) {
      loadPreviewUrls(selectedTestId);
      queryClient.invalidateQueries({ queryKey: ["admin-tests-360"] });
    }
    prevAllDoneRef.current = allDone;
  }, [allDone, uploadTotal, selectedTestId, loadPreviewUrls, queryClient]);

  const handleSegmentUpload = useCallback(
    async (frameIndex: number, file: File) => {
      const blob = await file
        .arrayBuffer()
        .then((b) => new Blob([b], { type: file.type }));
      enqueue(frameIndex, "side", blob);
      setViewerImagesByIndex((prev) => ({
        ...prev,
        [frameIndex]: {
          id: `pending-${frameIndex}`,
          url: URL.createObjectURL(blob),
        },
      }));
    },
    [enqueue],
  );

  const handleSelect360Test = useCallback(
    (testId: string, urls?: string[], test?: VehicleTest) => {
      setSelectedTestId(testId);
      setSelectedTest(test ?? allTests.find((t) => t.id === testId) ?? null);
      if (urls && urls.length > 0) {
        setViewerUrls(urls);
      } else {
        loadPreviewUrls(testId);
      }
      setTimeout(
        () => viewerSectionRef.current?.scrollIntoView({ behavior: "smooth" }),
        100,
      );
    },
    [loadPreviewUrls, allTests],
  );

  return (
    <div className="container py-8 max-w-2xl mx-auto">
      <h1 className="text-2xl font-semibold mb-2">{labels.title}</h1>
      <p className="text-sm text-muted-foreground mb-6">{labels.description}</p>

      <div className="space-y-6">
        <div>
          <label className="text-sm font-medium mb-2 block">
            {labels.vehicleTest}
          </label>
          <Popover
            open={testSelectOpen}
            onOpenChange={(open) => {
              setTestSelectOpen(open);
              if (!open) setTestSearchQuery("");
            }}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                )}
              >
                <span className="truncate">
                  {selectedTestId
                    ? (() => {
                        const t =
                          selectedTest ??
                          allTests.find((r) => r.id === selectedTestId);
                        return t
                          ? `${t.vehicleName} ${t.vehicleDetails?.regNumber || t.regNumber ? `(${t.vehicleDetails?.regNumber || t.regNumber})` : ""}`
                          : labels.selectTest;
                      })()
                    : labels.selectTest}
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[var(--radix-popover-trigger-width)] p-0"
              align="start"
            >
              <div className="flex items-center border-b px-2">
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                <Input
                  placeholder={labels.regNrPlaceholder}
                  value={testSearchQuery}
                  onChange={(e) => setTestSearchQuery(e.target.value)}
                  className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-9"
                />
              </div>
              <ScrollArea className="h-[min(280px,50vh)]">
                {filteredTests.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    {labels.noResults}
                  </p>
                ) : (
                  <div className="p-1">
                    {filteredTests.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => {
                          handleSelectTest(t.id, t);
                          setTestSelectOpen(false);
                          setTestSearchQuery("");
                        }}
                        className={cn(
                          "flex w-full items-center gap-2 rounded-sm py-1.5 pl-2 pr-2 text-left text-sm outline-none hover:bg-accent/20 focus:bg-accent/20",
                          selectedTestId === t.id && "bg-accent/20",
                        )}
                      >
                        <span className="shrink-0 flex h-3.5 w-3.5 items-center justify-center">
                          {selectedTestId === t.id ? (
                            <Check className="h-4 w-4 text-primary" />
                          ) : (
                            <span className="h-4 w-4" />
                          )}
                        </span>
                        <span className="flex flex-1 min-w-0 flex-col items-start gap-0.5">
                          <span className="truncate w-full">
                            {t.vehicleName}{" "}
                            {t.vehicleDetails?.regNumber || t.regNumber
                              ? `(${t.vehicleDetails?.regNumber || t.regNumber})`
                              : ""}
                          </span>
                          <span className="text-xs text-muted-foreground truncate w-full">
                            {getStatusLabel(t.status)}
                            {t.customer?.username &&
                              ` · ${t.customer.username}`}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </PopoverContent>
          </Popover>
        </div>

        <Button
          onClick={() => setModalOpen(true)}
          disabled={!selectedTestId}
          className="w-full sm:w-auto"
        >
          <Camera className="mr-2 h-4 w-4" />
          {labels.buttonLabel}
        </Button>

        <Exterior360CaptureModal
          open={modalOpen}
          onOpenChange={handleModalChange}
          carTestId={selectedTestId}
          initialFrameIndex={modalOpenForFrame}
          onUploadsComplete={handleModalUploadsComplete}
          uploadQueue={{
            enqueue,
            doneCount,
            failedCount,
            allDone,
            total: uploadTotal,
            queue,
          }}
        />

        <div className="border-t pt-6 mt-6">
          <div className="flex items-center justify-between gap-4 mb-1">
            <h2 className="text-lg font-medium">
              {labels.uploaded360ListTitle}
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                queryClient.invalidateQueries({ queryKey: ["admin-tests-360"] })
              }
              disabled={loading360List}
            >
              <RefreshCw
                className={`mr-1.5 h-4 w-4 ${loading360List ? "animate-spin" : ""}`}
              />
              {labels.refreshList}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {labels.uploaded360ListDescription}
          </p>
          {loading360List ? (
            <p className="text-sm text-muted-foreground">
              {labels.loading360List}
            </p>
          ) : testsWith360.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {labels.noUploaded360}
            </p>
          ) : (
            <ul className="space-y-2">
              {testsWith360.map(({ test, urls, imageCount }) => (
                <li key={test.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect360Test(test.id, urls, test)}
                    className={cn(
                      "w-full text-left px-4 py-3 rounded-lg border transition-colors flex items-center gap-3",
                      selectedTestId === test.id
                        ? "bg-accent/20 border-accent/50"
                        : "bg-card hover:bg-accent/50",
                    )}
                  >
                    <TbView360Number
                      className="h-5 w-5 shrink-0 text-muted-foreground"
                      aria-hidden
                    />
                    <span className="flex-1 min-w-0">
                      <span className="font-medium truncate block">
                        {test.vehicleName}{" "}
                        {test.vehicleDetails?.regNumber || test.regNumber
                          ? `(${test.vehicleDetails?.regNumber || test.regNumber})`
                          : ""}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {(imageCount === 1
                          ? (labels.imageCount ?? "{count} bild")
                          : (labels.imagesCount ?? "{count} bilder")
                        ).replace("{count}", String(imageCount))}
                        {test.customer?.username &&
                          ` · ${test.customer.username}`}
                      </span>
                    </span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {labels.view360} →
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {viewerUrls.length > 0 && (
          <div ref={viewerSectionRef} className="scroll-mt-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <h2 className="text-lg font-medium flex items-center gap-2">
                <TbView360Number className="h-5 w-5 shrink-0" aria-hidden />
                {labels.viewerTitle}
              </h2>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive shrink-0"
                onClick={handleDeleteAll360}
                disabled={isDeletingAll360}
              >
                {isDeletingAll360 ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-1.5" />
                )}
                {labels.deleteAll360 ?? "Radera alla 360-bilder"}
              </Button>
            </div>
            {selectedTestId &&
              (() => {
                const sel =
                  selectedTest ?? allTests.find((t) => t.id === selectedTestId);
                const reg = sel?.vehicleDetails?.regNumber || sel?.regNumber;
                return (
                  <p className="text-sm text-muted-foreground mb-2">
                    {sel?.vehicleName} {reg ? `(${reg})` : ""}
                  </p>
                );
              })()}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 md:items-stretch min-h-[280px]">
              {selectedTestId && (
                <div className="flex items-center justify-center min-h-[280px] md:min-h-0">
                  <Exterior360SegmentPicker
                    imagesByIndex={viewerImagesByIndex}
                    labels={{
                      segmentUploaded: labels.segmentUploaded,
                      segmentEmpty: labels.segmentEmpty,
                      viewFullscreen: labels.segmentViewFullscreen,
                      segmentViewFullscreen: labels.segmentViewFullscreen,
                      index: labels.segmentIndex,
                      imageLabel: labels.segmentImageLabel,
                      uploadImage: labels.segmentUploadImage,
                      openCamera: labels.segmentOpenCamera,
                      noImageFound: labels.noImageFound,
                      groupFrames: labels.groupFrames,
                      deleteImage: labels.deleteImage,
                    }}
                    onUpload={handleSegmentUpload}
                    onOpenCamera={(frameIndex) => {
                      setModalOpenForFrame(frameIndex);
                      setModalOpen(true);
                    }}
                    onViewFullscreen={(frameIndex, url, urlFull) =>
                      setSegmentFullscreenImage({
                        url: urlFull || url,
                        index: frameIndex,
                      })
                    }
                    onDelete={handleDeleteSegmentImage}
                    uploadingFrameIndices={uploadingFrameIndices}
                    deletingFrameIndices={
                      deletingFrameIndex != null ? [deletingFrameIndex] : []
                    }
                    uploadProgress={
                      uploadTotal > 0 && !allDone
                        ? { done: doneCount, total: uploadTotal }
                        : undefined
                    }
                    uploadProgressLabels={{
                      generating: labels.uploadGenerating,
                      progressPercent: labels.uploadProgressPercent,
                      timeEstimate: labels.uploadTimeEstimate,
                    }}
                    transparent
                    fill
                  />
                </div>
              )}
              <div className="min-h-[280px] md:min-h-0 flex flex-col">
                <Exterior360Viewer
                  imageUrls={viewerUrls}
                  labels={labels}
                  compact
                  fill
                  showFullscreenButton
                  onFullscreenClick={handleFullscreenClick}
                />
              </div>
            </div>
            <Dialog open={fullscreenOpen} onOpenChange={setFullscreenOpen}>
              <DialogContent className="w-fit max-w-[95vw] max-h-[95dvh] h-fit p-2 sm:p-4 flex flex-col bg-background overflow-hidden">
                <DialogTitle className="sr-only">
                  {labels.viewerTitle}
                </DialogTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-2 z-50"
                  onClick={() => setFullscreenOpen(false)}
                  aria-label={labels.exitFullscreen ?? "Stäng helskärm"}
                >
                  <X className="h-5 w-5" />
                </Button>
                {fullscreenLoading ? (
                  <div className="flex items-center justify-center text-muted-foreground w-[min(95vw,1200px)] min-h-[400px]">
                    {labels.loading}
                  </div>
                ) : fullscreenUrls.length > 0 ? (
                  <div className="flex flex-none">
                    <Exterior360Viewer
                      imageUrls={fullscreenUrls}
                      labels={labels}
                      className="w-[min(95vw,1400px)] shrink-0"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center text-muted-foreground w-[min(95vw,640px)] py-12">
                    {labels.noImages}
                  </div>
                )}
              </DialogContent>
            </Dialog>
            <Dialog
              open={!!segmentFullscreenImage}
              onOpenChange={(open) => !open && setSegmentFullscreenImage(null)}
            >
              <DialogContent className="max-w-[95vw] max-h-[95dvh] w-auto p-2 bg-black/95 border-none">
                {segmentFullscreenImage && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-2 z-50 text-white hover:bg-white/20"
                      onClick={() => setSegmentFullscreenImage(null)}
                      aria-label={labels.exitFullscreen}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                    <img
                      src={segmentFullscreenImage.url}
                      alt=""
                      className="max-w-full max-h-[90dvh] object-contain"
                    />
                    <p className="text-center text-sm text-slate-400 mt-1">
                      {labels.segmentImageLabel ?? "Bild"}{" "}
                      {segmentFullscreenImage.index + 1}
                    </p>
                  </>
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
    </div>
  );
}
